/**
 * Which language the page is in, and how that is spelled in the address bar.
 *
 * ## The URL is the locale
 *
 * The language lives in the path (`/en/planos`, `/es/planos`) rather than in a cookie,
 * a header or a stored preference, for three reasons that all matter here:
 *
 *  - A crawler gets a different document for each language instead of one document
 *    that swaps text after it has already decided what the page is about.
 *  - A reader can send somebody `/en/planos` and land on exactly that, in that
 *    language, without asking the recipient to share a preference first.
 *  - It cannot get out of step with the page. There is one source of truth, and it
 *    is the address bar the page is already rendering from.
 *
 * ## Portuguese is the bare path, and that is load-bearing
 *
 * pt-BR has no prefix. `/planos` is Portuguese; `/en/planos` is English.
 *
 * This is not a stylistic preference. Two addresses on this domain are written into
 * things outside this repository, and both of them are unprefixed:
 *
 *  - `/auth/callback` is the `redirect_to` both desktop apps put in their OAuth
 *    request, so Google sends the browser there. Prefix it and a released app build
 *    hands its login result to a 404.
 *  - `/conta/obrigado?order=…` is the `back_urls` entry Mercado Pago is configured
 *    with, so the buyer is returned there after paying.
 *
 * Requiring a prefix on those two would mean changing both integrations and shipping
 * new builds of both apps to ship a translated website. Making pt-BR the bare path
 * means they keep working untouched, and an unprefixed link from anywhere still
 * resolves to a complete Portuguese page rather than a redirect.
 *
 * ## What this deliberately does not do
 *
 * It does not redirect, and it does not guess from `Accept-Language`. A first-time
 * visitor who would rather read this in English gets Portuguese and a switcher
 * beside the wordmark. That is one extra click for somebody who already knows the
 * site, and in exchange no canonical URL ever moves, no deep link from an app or a
 * payment provider ever breaks, and no page is served a locale it did not ask for.
 */

export const LOCALES = ['pt-BR', 'en', 'es'] as const;

export type Locale = (typeof LOCALES)[number];

/**
 * The language an unprefixed address means.
 *
 * pt-BR rather than `en` because the site's original audience is Brazilian and the
 * bare path is what every existing link already points at. See the note above.
 */
export const DEFAULT_LOCALE: Locale = 'pt-BR';

export function isLocale(value: string): value is Locale {
  return (LOCALES as readonly string[]).includes(value);
}

/**
 * The path prefix a locale is spelled with, including its leading slash.
 *
 * Empty for pt-BR, which is what makes `/` and `/planos` Portuguese. Note the
 * consequence for the home page: `localePath('en', '/')` is `/en`, not `//`.
 */
export function localePrefix(locale: Locale): string {
  return locale === DEFAULT_LOCALE ? '' : `/${locale}`;
}

/**
 * The `<html lang>` value for a locale.
 *
 * A full BCP 47 tag rather than the two-letter code, because a screen reader uses it
 * to pick a pronunciation, and `pt-BR` and `pt-PT` are not pronounced the same way.
 */
export function htmlLang(locale: Locale): string {
  return locale;
}

/**
 * Separates a locale prefix from the rest of a path.
 *
 * The check is anchored to a whole leading segment rather than a `startsWith` on the
 * two letters, because `/english` and `/enterprise` are paths somebody could
 * reasonably want, and `/e` is not a language. Both are returned with the prefix
 * removed and a guaranteed leading slash, so a caller never has to normalise.
 *
 * `rest` for the bare path `/` is `/`, which keeps the round trip through
 * `localePath` exact.
 */
export function splitLocale(pathname: string): { readonly locale: Locale; readonly rest: string } {
  const normalized = pathname.replace(/\/+$/, '') || '/';

  const segments = normalized.split('/').filter((segment) => segment !== '');
  const head = segments[0];

  if (head !== undefined && head !== 'pt-BR' && isLocale(head)) {
    const rest = `/${segments.slice(1).join('/')}`.replace(/\/+$/, '') || '/';
    return { locale: head, rest };
  }

  // `/pt-BR` is accepted as an explicit spelling of the default, so a link built by
  // hand with the prefix included still lands on Portuguese rather than falling
  // through to a page called "pt-BR" that does not exist.
  if (head === 'pt-BR') {
    const rest = `/${segments.slice(1).join('/')}`.replace(/\/+$/, '') || '/';
    return { locale: DEFAULT_LOCALE, rest };
  }

  return { locale: DEFAULT_LOCALE, rest: normalized };
}

/**
 * Puts a locale back onto a locale-free path.
 *
 * The inverse of `splitLocale`, and total: `localePath(...splitLocale(path))` returns
 * a path that resolves to the same page, which is what makes the language switcher
 * safe to build by swapping the prefix on wherever the reader already is.
 */
export function localePath(locale: Locale, rest: string): string {
  const prefix = localePrefix(locale);
  const bare = (rest.startsWith('/') ? rest : `/${rest}`).replace(/\/+$/, '') || '/';

  // The bare home page is the one place a prefix would be followed by a slash and
  // nothing else, producing `/en/` where the rest of the site writes `/en`.
  if (bare === '/') return prefix === '' ? '/' : prefix;
  return `${prefix}${bare}`;
}

/**
 * The switcher's own label for a locale, in that locale.
 *
 * A language switcher that labels English as "Inglês" is not wrong, but it is
 * useless to somebody who does not read Portuguese. Every label here is the name the
 * language uses for itself, which is why the Portuguese entry is `Português` and not
 * `Portuguese`.
 */
export function localeLabel(locale: Locale): string {
  switch (locale) {
    case 'pt-BR':
      return 'Português';
    case 'en':
      return 'English';
    case 'es':
      return 'Español';
  }
}

/**
 * The short form used in the header, where three full words do not fit.
 *
 * The two non-default locales are already two letters. Portuguese gets `PT`, which is
 * how it is written everywhere else on this site.
 */
export function localeShortLabel(locale: Locale): string {
  switch (locale) {
    case 'pt-BR':
      return 'PT';
    case 'en':
      return 'EN';
    case 'es':
      return 'ES';
  }
}

/**
 * The locale to hand `Intl`.
 *
 * A site locale is already a full BCP 47 tag, so it is passed straight through. This
 * exists as a named function only to keep the two uses apart: dates are formatted for
 * the reader's language, while *currency* is formatted for the currency, and those are
 * not the same decision. Pricing keeps its own table for the second one and must not
 * come looking for it here.
 */
export function intlLocale(locale: Locale): string {
  return locale;
}